import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PaginationPage, PaginationResponse, ResponseWrapper } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { TokenService } from '../../shared/services/token.service';
import { Offer, OfferItem, OfferStatus, OfferTransaction } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class OfferService {

  private _allOfferStatusLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
  allOfferStatusLoaded$ = this._allOfferStatusLoaded.asObservable();

  private _offers: { [key: number]: ReplaySubject<Offer> } = { };
  offers$: Observable<Offer[]> = combineLatest(Object.values(this._offers).map((x: ReplaySubject<Offer>) => x.asObservable()));

  private _offerStatus: { [key: number]: ReplaySubject<OfferStatus> } = { };

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  createOffer(offer: Offer, note: string = null): Observable<Offer> {
    return this._httpClient.post(`${serverLocation}/api/offer`, {
      note: note,
      content: offer
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Offer>;
  }
  /**
   * the method creates a new offer item
   * @param item 
   * @returns created offer item
   */
  createOfferItem(item: OfferItem, auditNote: string = null): Observable<OfferItem> {
    return this._httpClient.post(`${serverLocation}/api/offer-item`, {
      content: item,
      note: auditNote
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<OfferItem>;
  }

  deleteOffer(offerId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/offer/${offerId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  deleteOfferItem(itemId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/offer-item/${itemId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }
  
  getAllOfferStatus(soft: boolean = true): Observable<OfferStatus[]> {
    if (!soft || !this._allOfferStatusLoaded.value) {
      return this._getAllOfferStatus().pipe(
        tap(
          (status: OfferStatus[]) => {
            status.forEach((x: OfferStatus) => {
              if (!soft && this._offerStatus[x.offerstateid]) this._offerStatus[x.offerstateid].next(x);
              else {
                if (!this._offerStatus[x.offerstateid]) this._offerStatus[x.offerstateid] = new ReplaySubject();
                this._offerStatus[x.offerstateid].next(x);
              }
            });
            this._allOfferStatusLoaded.next(true);
          }
        )
      );
    }
    return combineLatest(Object.values(this._offerStatus));
  }

  getOfferById(identifier: number, soft: boolean = true): Observable<Offer> {
    if (typeof identifier !== 'number') return of(null);
    if (!soft || !this._offers[identifier]) {
      if (!this._offers[identifier]) this._offers[identifier] = new ReplaySubject(1);
      this._getOfferById(identifier).subscribe({
        next: (offer: Offer) => this._offers[identifier].next(offer),
        error: (error: HttpErrorResponse) => this._offers[identifier].error(error)
      });
    }
    return this._offers[identifier].asObservable();
  }

  /**
   * the methos returns an offer transaction by it's identifier
   * @param offerId
   * @returns 
   */
  getOfferTransactionById(offerId: number): Observable<OfferTransaction> {
    return this._httpClient.get(`${serverLocation}/api/offer-transaction/${offerId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<OfferTransaction>;
  }

  /**
   * the method returns a set of offer transactions available for the passed offerstate
   * @param stateId 
   * @returns 
   */
  getOfferTransactionsForState(stateId: number): Observable<ResponseWrapper<OfferTransaction[]>> {
    return this._httpClient.get(`${serverLocation}/api/offer-transaction/state/${stateId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ResponseWrapper<OfferTransaction[]>>;
  }

  searchOffersByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/offer/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<Offer[]>>;
  }

  updateOffer(offer: Offer, note: string = null): Observable<Offer> {
    return this._httpClient.put(`${serverLocation}/api/offer`, {
      note: note,
      content: offer
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Offer>;
  }

  updateOfferItem(offerItem: OfferItem, note: string = null): Observable<OfferItem> {
    return this._httpClient.put(`${serverLocation}/api/offer-item`, {
      note: note,
      content: offerItem
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<OfferItem>;
  }

  private _getAllOfferStatus(): Observable<OfferStatus[]> {
    return (this._httpClient.get(`${serverLocation}/api/offer-status`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<OfferStatus[]>);
  }

  private _getOfferById(identifier: number): Observable<Offer> {
    return this._httpClient.get(`${serverLocation}/api/offer/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Offer>;
  }

}
